const DEFAULT_STATE = Object.freeze({ audience: 'all', category: 'all', status: 'all', sort: 'featured' });
const ALLOWED = Object.freeze({
  audience: new Set(['all', 'men', 'women']),
  category: new Set(['all', 'upper', 'lower', 'full', 'towel', 'other']),
  status: new Set(['all', 'live', 'soldout']),
  sort: new Set(['featured', 'price-asc', 'price-desc']),
});

export function normaliseCollectionState(search = '') {
  const params = new URLSearchParams(search);
  const legacyAudience = ['men', 'women'].includes(params.get('category')) ? params.get('category') : null;
  const nativeAvailabilityValues = new Set(
    params.getAll('filter.v.availability').filter((value) => value === '0' || value === '1')
  );
  let nativeStatus = null;
  if (nativeAvailabilityValues.size > 1) nativeStatus = 'all';
  else if (nativeAvailabilityValues.has('1')) nativeStatus = 'live';
  else if (nativeAvailabilityValues.has('0')) nativeStatus = 'soldout';
  const candidate = {
    audience: legacyAudience || params.get('audience') || DEFAULT_STATE.audience,
    category: legacyAudience ? 'all' : params.get('category') || DEFAULT_STATE.category,
    status: nativeStatus ?? params.get('status') ?? DEFAULT_STATE.status,
    sort: params.get('sort') || DEFAULT_STATE.sort,
  };
  return Object.fromEntries(Object.entries(candidate).map(([key, value]) => [key, ALLOWED[key].has(value) ? value : DEFAULT_STATE[key]]));
}

export function matchesCollectionCard(dataset, state) {
  const audience = dataset.audience || (dataset.group === 'women' ? 'women' : 'men');
  const category = dataset.category || dataset.group;
  const audienceMatches = !state.audience || state.audience === 'all' || audience === 'unisex' || audience === state.audience;
  const categoryMatches = state.category === 'all' || category === state.category;
  const statusMatches = state.status === 'all' || dataset.status === state.status;
  return audienceMatches && categoryMatches && statusMatches;
}

export function searchForCollectionState(search = '', state = normaliseCollectionState(search)) {
  const params = new URLSearchParams(search);
  for (const key of ['graphic', 'colour', 'status', 'filter.v.availability']) params.delete(key);
  for (const key of ['audience', 'category', 'sort']) {
    if (!state[key] || state[key] === DEFAULT_STATE[key]) params.delete(key);
    else params.set(key, state[key]);
  }
  if (state.status === 'live') params.set('filter.v.availability', '1');
  else if (state.status === 'soldout') params.set('filter.v.availability', '0');
  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

export function visibleCollectionCards(cards, state) {
  const visible = cards.filter((card) => matchesCollectionCard(card.dataset, state));
  return { cards: visible, zeroResults: visible.length === 0 };
}

export function sortCollectionCards(cards, sort) {
  const editorialOrder = (card) => Number(card.dataset.order);
  const price = (card) => {
    const rawValue = card.dataset.price;
    const value = Number(rawValue);
    return typeof rawValue === 'string' && rawValue.trim() !== '' && Number.isFinite(value) ? value : null;
  };
  return cards
    .map((card, index) => ({ card, index, price: price(card) }))
    .sort((left, right) => {
      if (sort === 'price-asc' || sort === 'price-desc') {
        const leftPriced = left.price !== null;
        const rightPriced = right.price !== null;
        if (leftPriced && rightPriced) {
          const difference = sort === 'price-asc' ? left.price - right.price : right.price - left.price;
          if (difference !== 0) return difference;
        } else if (leftPriced !== rightPriced) {
          return leftPriced ? -1 : 1;
        }
      }
      return editorialOrder(left.card) - editorialOrder(right.card) || left.index - right.index;
    })
    .map(({ card }) => card);
}

export function markFirstVisibleCollectionGroup(groups) {
  const visible = groups.find((group) => !group.hidden) ?? null;
  for (const group of groups) group.classList.toggle('is-first-visible', group === visible);
  return visible;
}

const CatalogElement = globalThis.HTMLElement || class {};

class AlvornCollectionCatalog extends CatalogElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true') return;
    this.dataset.initialised = 'true';
    this.state = normaliseCollectionState(window.location.search);
    this.boundPopState = () => { this.state = normaliseCollectionState(window.location.search); this.apply(); };
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));
    window.addEventListener('popstate', this.boundPopState);
    try {
      this.apply();
      for (const control of this.querySelectorAll('[data-alvorn-audience], [data-alvorn-category-select], [data-alvorn-status], [data-alvorn-reset], [data-alvorn-sort]')) control.removeAttribute('disabled');
      this.classList.add('is-enhanced');
    } finally {
      delete this.dataset.initialAudience;
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.boundPopState);
  }

  refresh() {
    this.state = normaliseCollectionState(window.location.search);
    this.apply();
  }

  onClick(event) {
    const audience = event.target.closest('[data-alvorn-audience]');
    if (audience) {
      this.state.audience = audience.value;
      const cards = [...this.querySelectorAll('[data-alvorn-catalog-card]')];
      if (!cards.some((card) => matchesCollectionCard(card.dataset, { ...this.state, status: 'all' }))) this.state.category = 'all';
      this.apply();
      return;
    }
    if (event.target.closest('[data-alvorn-reset]')) {
      this.state = { ...DEFAULT_STATE, sort: this.state.sort };
      this.querySelector('.alc__filter-menu')?.removeAttribute('open');
      this.apply();
      return;
    }
    const toggle = event.target.closest('[data-alvorn-view-toggle]');
    if (toggle) {
      const card = toggle.closest('[data-alvorn-catalog-card]');
      const active = card.classList.toggle('is-secondary');
      const currentLabel = toggle.getAttribute('aria-label') || '';
      const labelPrefix = currentLabel.replace(/\s+—\s+(?:ilk|ikinci) görünümü göster$/iu, '');
      const actionLabel = active ? 'ilk görünümü göster' : 'ikinci görünümü göster';
      toggle.setAttribute('aria-pressed', String(active));
      toggle.setAttribute('aria-label', labelPrefix ? `${labelPrefix} — ${actionLabel}` : actionLabel);
    }
  }

  onChange(event) {
    if (event.target.matches('[data-alvorn-sort]')) this.state.sort = event.target.value;
    else if (event.target.matches('[data-alvorn-category-select]')) this.state.category = event.target.value;
    else if (event.target.matches('[data-alvorn-status]')) this.state.status = event.target.value;
    else return;
    this.apply();
  }

  apply(updateUrl = true) {
    const cards = [...this.querySelectorAll('[data-alvorn-catalog-card]')];
    if (this.dataset.lockedAudience) this.state.audience = this.dataset.lockedAudience;
    const categorySelect = this.querySelector('[data-alvorn-category-select]');
    if (categorySelect) {
      for (const option of categorySelect.options) {
        const available = option.value === 'all' || cards.some((card) => matchesCollectionCard(card.dataset, { ...this.state, category: option.value, status: 'all' }));
        option.hidden = !available;
        option.disabled = !available;
      }
      if (categorySelect.querySelector(`option[value="${this.state.category}"]`)?.disabled) this.state.category = 'all';
      categorySelect.value = this.state.category;
    }
    const visibility = visibleCollectionCards(cards, this.state);
    const visibleCards = new Set(visibility.cards);
    for (const card of cards) {
      card.hidden = !visibleCards.has(card);
    }

    const groups = [...this.querySelectorAll('[data-alvorn-catalog-group]')];
    for (const group of groups) {
      const grid = group.querySelector('[data-alvorn-catalog-grid]');
      const groupCards = [...grid.querySelectorAll('[data-alvorn-catalog-card]')];
      for (const card of sortCollectionCards(groupCards, this.state.sort)) grid.append(card);
      group.hidden = !groupCards.some((card) => !card.hidden);
    }
    markFirstVisibleCollectionGroup(groups);

    for (const button of this.querySelectorAll('[data-alvorn-audience]')) button.setAttribute('aria-pressed', String(button.value === this.state.audience));
    for (const input of this.querySelectorAll('[data-alvorn-status]')) input.checked = input.value === this.state.status;
    const sort = this.querySelector('[data-alvorn-sort]');
    if (sort) sort.value = this.state.sort;
    const count = this.querySelector('[data-alvorn-result-count]');
    if (count) count.textContent = `${visibility.cards.length} seçenek`;
    const zeroResults = this.querySelector('[data-alvorn-zero-results]');
    if (zeroResults) zeroResults.hidden = !visibility.zeroResults;
    const inner = this.querySelector('.alc__inner');
    if (inner) inner.classList.toggle('is-empty', visibility.zeroResults);
    this.classList.toggle('is-empty', visibility.zeroResults);
    if (updateUrl) this.updateUrl();
  }

  updateUrl() {
    const url = new URL(window.location.href);
    const urlState = this.dataset.lockedAudience ? { ...this.state, audience: DEFAULT_STATE.audience } : this.state;
    url.search = searchForCollectionState(url.search, urlState);
    history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
}

if (globalThis.customElements && !customElements.get('alvorn-collection-catalog')) customElements.define('alvorn-collection-catalog', AlvornCollectionCatalog);

globalThis.document?.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('alvorn-collection-catalog').forEach((catalog) => catalog.refresh());
});
